// This file defines a DataContext for managing shared data across components in a React application.
// It includes a DataProvider component that wraps around the application to provide data context.
// The useData hook allows components to access the data context easily.

import React, { createContext, useContext, useState } from 'react'

// Define the shape of the data context
interface DataContextType {
  data: any
  setData: React.Dispatch<React.SetStateAction<any>> // Function to update the data
}

// Create the DataContext with an undefined initial value
const DataContext = createContext<DataContextType | undefined>(undefined)

// DataProvider component that wraps the application
const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<any>() // State to hold the data

  return <DataContext.Provider value={{ data, setData }}>{children}</DataContext.Provider>
}

// Custom hook to use the DataContext
const useData = () => {
  const context = useContext(DataContext)
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider') // Error if used outside of DataProvider
  }
  return context // Return the context
}

// Export the DataProvider and useData hook for use in other components
export { DataProvider, useData }
